import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface SeasonalPeriod {
  month: string;
  conditions: 'optimal' | 'moderate' | 'challenging';
  description: string;
  icon: string;
}

interface SeasonalCalendarProps {
  periods: SeasonalPeriod[];
}

const SeasonalCalendar = ({ periods }: SeasonalCalendarProps) => {
  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'optimal':
        return 'bg-success/10 border-success text-success';
      case 'moderate':
        return 'bg-warning/10 border-warning text-warning';
      case 'challenging':
        return 'bg-destructive/10 border-destructive text-destructive';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      <div className="mb-6">
        <h3 className="font-headline font-bold text-xl text-foreground mb-2">
          Seasonal Logistics Calendar
        </h3>
        <p className="font-body text-sm text-muted-foreground">
          Plan your shipments around regional weather patterns and road conditions
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {periods.map((period, idx) => (
          <div
            key={idx}
            className={`p-4 rounded-lg border-2 transition-all duration-300 hover:shadow-elevation-1 ${getConditionColor(
              period.conditions
            )}`}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-headline font-bold text-base">
                {period.month}
              </h4>
              <Icon name={period.icon as any} size={20} />
            </div>
            <p className="font-body text-sm leading-relaxed">
              {period.description}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex flex-wrap gap-4 justify-center">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-success/20 border-2 border-success" />
            <span className="text-sm font-body text-muted-foreground">
              Optimal Conditions
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-warning/20 border-2 border-warning" />
            <span className="text-sm font-body text-muted-foreground">
              Moderate Delays
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-destructive/20 border-2 border-destructive" />
            <span className="text-sm font-body text-muted-foreground">
              Challenging Routes
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonalCalendar;